import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { ResolvedRegimeMappings } from "@idwal/idwal-utilities/lib/types/RegimeMapping";
import AuthToken from "./AuthToken";
import {
    toDefectCodeChips,
    toRegimeDescriptions
} from "../helpers/regimeMappings";

const REGIME_MAPPINGS_KEY = "regimeMappings";

// The mappings change only when defect-service's files are re-uploaded, which
// is rare, and nothing signals it: there is no version to check against. A day
// of staleness is the accepted cost of not refetching them on every tab.
const ONE_DAY = 24 * 60 * 60 * 1000;

/**
 * Every Idwal defect code's regime codes, already joined to what each one
 * means.
 *
 * Fetched through report-api rather than from idwal-api, whose regime routes
 * sit behind a Cognito authoriser: neither buyers nor sellers hold a Cognito
 * token here, so report-api fetches them as itself and serves them to the
 * same token every other report tab uses.
 *
 * Throws rather than resolving empty when there is no token or the call
 * fails, so the query errors and the chips fall back to the bare Idwal code.
 */
export const fetchRegimeMappings = async (): Promise<ResolvedRegimeMappings> => {
    const token = AuthToken();

    if (!token) {
        throw new Error("No access token to request the regime mappings with.");
    }

    const { data } = await axios.get<ResolvedRegimeMappings>(
        `${process.env.REACT_APP_API_URL}regime-mappings`,
        { headers: { Authorization: `Bearer ${token}` } }
    );

    return data;
};

/**
 * The regime codes shown against each defect, and what each one is.
 *
 * Both are derived from one fetch: taking them separately would mean the chips
 * render before their tooltips exist. A failed fetch leaves both undefined,
 * which renders the Idwal defect code alone - what happened before regime
 * codes existed. A fetch whose descriptions were unavailable upstream still
 * renders the chips, just without tooltips.
 *
 * Keyed globally rather than by inspection: the mappings are one reference
 * set, so the Defects and Grading tabs, and every inspection, share one copy.
 */
export const getDefectCodeChips = (
    releaseAuthorityMappings: boolean | undefined
) => {
    const queryClient = useQueryClient();
    const enabled = Boolean(releaseAuthorityMappings);

    const { data } = useQuery<ResolvedRegimeMappings>(
        REGIME_MAPPINGS_KEY,
        fetchRegimeMappings,
        {
            // The flag is undefined until LaunchDarkly resolves it, so this
            // waits rather than fetching for a user who should not see the codes.
            enabled,
            staleTime: ONE_DAY,
            cacheTime: ONE_DAY
        }
    );

    // Disabling a query does not clear what it already holds, so without this a
    // flag turned off mid-session leaves the chips on screen until the tab
    // unmounts. The tabs render only the active one, so that may never happen.
    useEffect(() => {
        if (!enabled) {
            queryClient.removeQueries(REGIME_MAPPINGS_KEY);
        }
    }, [enabled, queryClient]);

    return useMemo(
        () => ({
            defectCodes: toDefectCodeChips(data),
            descriptions: toRegimeDescriptions(data)
        }),
        [data]
    );
};
